import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { PageTitle } from '../../components/pageTitle.tsx';
import { ProtocolForm } from '../../components/protocol-form/protocolForm.tsx';
import { ProtocolPdf } from '../../components/protocol-pdf/protocolPdf.tsx';
import { VisualInspectionImage } from '../../components/visualInspectionImage.tsx';
import { useConfirmOnLeave } from '../../hooks/useConfirmOnLeave.ts';
import { ProtocolStoreProvider, useProtocolStore } from './protocolStoreProvider.tsx';
import { useImagesState } from './useImagesState.ts';
import { useInitProtocol } from './useInitProtocol.ts';
import { usePdfState } from './usePdfState.ts';
import { usePositionFilter } from './usePositionFilter.ts';
import { useSaveProtocol } from './useSaveProtocol.ts';


export function ProtocolView() {
    return (
        <ProtocolStoreProvider>
            <Content />
        </ProtocolStoreProvider>
    );
}


function Content() {
    const [showAllMarkers, setShowAllMarkers] = useState(false);
    const [showAllPosNrs, setShowAllPosNrs] = useState(false);
    const isFinalSavedRef = useRef(false);

    const protocol = useProtocolStore(state => state.protocol);
    const setProtocol = useProtocolStore(state => state.setProtocol);
    const addPosition = useProtocolStore(state => state.addPosition);
    const addMarker = useProtocolStore(state => state.addMarker);
    const setMarkerLocation = useProtocolStore(state => state.setMarkerLocation);
    const positionSelection = useProtocolStore(state => state.positionSelection);
    const isProtocolDirty = useProtocolStore(state => state.isDirty);

    const location = useLocation();
    const navigate = useNavigate();


    const {
        isProtocolLoading,
        protocolError,
        mode,
        isViewMode,
        initialProtocolId,
        baseProtocol,
    } = useInitProtocol(protocol, setProtocol);

    const {
        isPdfLoading,
        isPdfError,
        pdfData,
        pdfDataToUpload,
        handleSetPdfFile,
        handlePdfDataUploaded,
    } = usePdfState(initialProtocolId);

    const {
        selectedPositionImage,
        imagesToUpload,
        handleImageChange,
        handleNewImageUploaded,
    } = useImagesState(protocol, positionSelection);

    const {
        positionFilter,
        filterOptions,
        visiblePositionIds,
        setPositionFilter,
    } = usePositionFilter(protocol?.positions ?? [], protocol?.measurementPlan);

    const {
        isSaving,
        isFinalSaving,
        handleDraftSave,
        handleFinalSave,
        handleReviewSave,
    } = useSaveProtocol({
        baseProtocol,
        pdfToUpload: pdfDataToUpload,
        onPdfUploaded: handlePdfDataUploaded,
        imagesToUpload,
        onImageUploaded: handleNewImageUploaded,
        isViewMode,
        isFinalSavedRef,
        onFinalSaved: () => handleClose(),
        onReviewSaved: () => handleClose(),
    });

    const isDirty = isProtocolDirty || !!pdfDataToUpload || imagesToUpload.length > 0;
    useConfirmOnLeave(() => isDirty && !isFinalSavedRef.current, !isViewMode);

    function handleClose() {
        const returnPath = location.state?.returnTo;
        const fallbackPath = '/protocol-list';
        navigate(returnPath || fallbackPath);
    }


    const activePosition = positionSelection ? protocol?.positions[positionSelection.positionIdx] : null;
    const shouldShowImagePane = activePosition?.type === 'visualInspection';

    if (isProtocolLoading) return <div>Loading...</div>;
    if (protocolError) return <div>An error has occurred: {protocolError.message}</div>;
    return (
        <>
            <PageTitle title='Protocol' />
            <div className='measurement-container'>
                <ProtocolPdf
                    className='measurement-main-pane measurement-pdf-container'
                    isViewMode={isViewMode}
                    positions={protocol?.positions}
                    visiblePositionIds={visiblePositionIds}
                    positionSelection={positionSelection}
                    showAllMarkers={showAllMarkers}
                    showAllPosNrs={showAllPosNrs}
                    pdfContent={pdfData}
                    isPdfLoading={isPdfLoading}
                    isPdfError={isPdfError}
                    onAddPosition={addPosition}
                    onUpdateLocation={setMarkerLocation}
                    onAddMarker={addMarker}
                />

                <div className='measurement-side-pane measurement-side-pane--protocol measurement-form-pane '>
                    {protocol && <ProtocolForm
                        className='measurement-form-top'
                        mode={mode}
                        pdfData={pdfData}
                        onSetPdfFile={handleSetPdfFile}
                        showAllMarkers={showAllMarkers}
                        setShowAllMarkers={setShowAllMarkers}
                        showAllPosNrs={showAllPosNrs}
                        setShowAllPosNrs={setShowAllPosNrs}
                        positionFilter={positionFilter}
                        filterOptions={filterOptions}
                        visiblePositionIds={visiblePositionIds}
                        onPositionFilterChange={setPositionFilter}
                        isDirty={isDirty}
                        isSaving={isSaving}
                        isFinalSaving={isFinalSaving}
                        onDraftSave={handleDraftSave}
                        onFinalSave={handleFinalSave}
                        onReviewSave={handleReviewSave}
                        onCloseClicked={handleClose}
                    />}

                    {shouldShowImagePane && <VisualInspectionImage
                        className='measurement-form-bottom'
                        imageData={selectedPositionImage}
                        isViewMode={isViewMode}
                        onImageChange={handleImageChange}
                    />}
                </div>
            </div>
        </>
    );
}
